import { Box, Typography, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReportDataGrid from '../../../components/Common/ReportDataGrid';
import { formatWeight, formatPercent, formatDate, formatGwpBasis, exportRowsToCsv } from '../../../components/Common/ReportDataGrid/utils';
import { trakrefBlue } from '../../../color_utils';

const ServiceBasedTable = ({ rows, totalEmissionsLbs, totalEmissionsCo2e, loading }) => {
    const columns = [
        {
            field: 'location',
            headerName: 'Location',
        },
        {
            field: 'serviceEventId',
            headerName: 'Service Event ID',
        },
        {
            field: 'serviceDate',
            headerName: 'Service Date',
            valueFormatter: formatDate,
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
        },
        {
            field: 'assetId',
            headerName: 'Asset ID',
        },
        {
            field: 'assetStatus',
            headerName: 'Asset Status',
        },
        {
            field: 'operationalStatus',
            headerName: 'Operational Status',
        },
        {
            field: 'assetType',
            headerName: 'Asset Type',
        },
        {
            field: 'materialType',
            headerName: 'Material Type',
        },
        {
            field: 'chargecapacityLbs',
            headerName: 'Charge Capacity (lbs)',
            type: 'number',
            valueFormatter: formatWeight,
        },
        {
            field: 'serviceEventLeakRate',
            headerName: 'Service Event Leak Rate (%)',
            type: 'number',
            valueFormatter: formatPercent(1),
        },
        {
            field: 'netGasAddedLbs',
            headerName: 'Net Gas Added (lbs)',
            type: 'number',
            valueFormatter: formatWeight,
        },
        {
            field: 'gwp',
            headerName: 'GWP',
            type: 'number',
            valueFormatter: formatGwpBasis,
        },
        {
            field: 'emissionsCo2e',
            headerName: 'Emissions (lbs CO2e)',
            type: 'number',
            valueFormatter: formatWeight,
        },
    ];

    const getRowId = (row) => `${row.serviceEventId}-${row.assetId}`;

    // US 16953 AC7: counted from the rows, which already carry the flag.
    const gwpBasisUnknownCount = rows.filter(r => r.gwpBasisUnknown).length;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Service-Based Emissions (Leak Repairs &amp; Initial Charges)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Refrigerant Usage/Emissions by Service Event (excludes service events with 0 net gas added)
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => exportRowsToCsv(columns, rows, 'service_based_emissions.csv')}
                    sx={{ backgroundColor: trakrefBlue, '&:hover': { backgroundColor: trakrefBlue }, flexShrink: 0 }}
                    disabled={rows.length === 0}
                >
                    Export CSV
                </Button>
            </Box>
            <ReportDataGrid
                rows={rows}
                columns={columns}
                getRowId={getRowId}
                loading={loading}
                noRowsLabel="No service events found. Try adjusting your filters."
                initialState={{
                    sorting: { sortModel: [{ field: 'serviceDate', sort: 'desc' }] }
                }}
            />
            {gwpBasisUnknownCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2 }}>
                    {gwpBasisUnknownCount} of {rows.length} rows have no recorded value for the selected GWP basis. Their GWP shows as an em-dash and they contribute 0 to the totals below.
                </Typography>
            )}
            {rows.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 4, pr: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Total Net Gas Added: {Math.round(totalEmissionsLbs).toLocaleString()} lbs
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Total Emissions: {Math.round(totalEmissionsCo2e).toLocaleString()} lbs CO2e
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ServiceBasedTable;
